import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { mcsApi } from '../../../api/mcs/mcsApi';
import {
    IShipmentDraftResponseModel,
    ICreateDraftFromOrdersModel,
    IUpdateDraftOrdersModel,
    IBuildShipmentFromDraftModel,
    IDraftShipmentOrderModel,
} from '../../../api/mcs/interfaces';
import { IShipment } from '../../../api/shipment/interfaces';
import { notifyError, notifySuccess } from '../../../api/axios';
import { RootState } from '../../store';
import { t } from 'i18next';

interface DraftsState {
    draftsList: IShipmentDraftResponseModel[];
    currentDraft: IShipmentDraftResponseModel | null;
    suggestedOrders: IDraftShipmentOrderModel[];
    builtShipment: IShipment | null;
    loading: boolean;
    fetchingDraft: boolean;
    creatingDraft: boolean;
    updatingDraft: boolean;
    buildingShipment: boolean;
    fetchingSuggestions: boolean;
    error: string | null;
}

const initialState: DraftsState = {
    draftsList: [],
    currentDraft: null,
    suggestedOrders: [],
    builtShipment: null,
    loading: false,
    fetchingDraft: false,
    creatingDraft: false,
    updatingDraft: false,
    buildingShipment: false,
    fetchingSuggestions: false,
    error: null,
};

export const fetchDrafts = createAsyncThunk(
    'mcs/drafts/fetchAll',
    async (
        {
            pageSize = 50,
            pageNumber = 1,
        }: { pageSize?: number; pageNumber?: number },
        { rejectWithValue },
    ) => {
        try {
            const drafts = await mcsApi.drafts.getDrafts(pageSize, pageNumber);
            return drafts;
        } catch (error: unknown) {
            notifyError(t('MCS:failedToFetchShipmentDrafts'));
            const message =
                error instanceof Error
                    ? error.message
                    : t('MCS:failedToFetchShipmentDrafts');
            return rejectWithValue(message);
        }
    },
);

export const fetchDraftById = createAsyncThunk(
    'mcs/drafts/fetchById',
    async (draftId: number, { rejectWithValue }) => {
        try {
            const draft = await mcsApi.drafts.getDraftById(draftId);
            return draft;
        } catch (error: unknown) {
            notifyError(`${t('MCS:failedToFetchShipmentDraft')} ${draftId}`);
            const message =
                error instanceof Error
                    ? error.message
                    : `${t('MCS:failedToFetchShipmentDraft')} ${draftId}`;
            return rejectWithValue(message);
        }
    },
);

export const createDraftFromOrders = createAsyncThunk(
    'mcs/drafts/create',
    async (model: ICreateDraftFromOrdersModel, { rejectWithValue }) => {
        try {
            const draft = await mcsApi.drafts.createDraftFromOrders(model);
            notifySuccess(t('MCS:shipmentSavedAsDraft'));
            return draft;
        } catch (error: unknown) {
            notifyError(t('MCS:shipmentDraftCreationFailure'));
            const message =
                error instanceof Error
                    ? error.message
                    : t('MCS:shipmentDraftCreationFailure');
            return rejectWithValue(message);
        }
    },
);

export const updateDraftOrders = createAsyncThunk(
    'mcs/drafts/update',
    async (
        { draftId, model }: { draftId: number; model: IUpdateDraftOrdersModel },
        { rejectWithValue },
    ) => {
        try {
            const draft = await mcsApi.drafts.updateDraftOrders(draftId, model);
            notifySuccess(t('MCS:shipmentDraftUpdateSuccess'));
            return draft;
        } catch (error: unknown) {
            notifyError(t('MCS:shipmentDraftUpdateFailure'));
            const message =
                error instanceof Error
                    ? error.message
                    : t('MCS:shipmentDraftUpdateFailure');
            return rejectWithValue(message);
        }
    },
);

export const buildShipmentFromDraft = createAsyncThunk(
    'mcs/drafts/build',
    async (
        {
            draftId,
            model,
        }: { draftId: number; model?: IBuildShipmentFromDraftModel },
        { rejectWithValue },
    ) => {
        try {
            const shipment = await mcsApi.drafts.buildShipmentFromDraft(
                draftId,
                model,
            );
            notifySuccess(t('MCS:shipmentBuiltFromSuccess'));
            return shipment;
        } catch (error: unknown) {
            notifyError(t('MCS:shipmentBuiltFromFailure'));
            const message =
                error instanceof Error
                    ? error.message
                    : t('MCS:shipmentBuiltFromFailure');
            return rejectWithValue(message);
        }
    },
);

export const fetchSuggestedOrders = createAsyncThunk(
    'mcs/drafts/fetchSuggestions',
    async (draftId: number, { rejectWithValue }) => {
        try {
            const orders = await mcsApi.drafts.getSuggestedOrders(draftId);
            return orders;
        } catch (error: unknown) {
            notifyError(t('MCS:failedToFetchSuggestedOrders'));
            const message =
                error instanceof Error
                    ? error.message
                    : t('MCS:failedToFetchSuggestedOrders');
            return rejectWithValue(message);
        }
    },
);

export const deleteDraft = createAsyncThunk(
    'mcs/drafts/delete',
    async (draftId: number, { rejectWithValue }) => {
        try {
            await mcsApi.drafts.deleteDraft(draftId);
            notifySuccess(t('MCS:draftShipmentDeletionSuccess'));
            return draftId;
        } catch (error: unknown) {
            notifyError(t('MCS:draftShipmentDeletionFailure'));
            const message =
                error instanceof Error
                    ? error.message
                    : t('MCS:draftShipmentDeletionFailure');
            return rejectWithValue(message);
        }
    },
);

const draftsSlice = createSlice({
    name: 'mcsDrafts',
    initialState,
    reducers: {
        clearCurrentDraft: state => {
            state.currentDraft = null;
            state.suggestedOrders = [];
        },
        clearBuiltShipment: state => {
            state.builtShipment = null;
        },
        clearSuggestedOrders: state => {
            state.suggestedOrders = [];
        },
        clearError: state => {
            state.error = null;
        },
    },
    extraReducers: builder => {
        builder.addCase(fetchDrafts.pending, state => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(fetchDrafts.fulfilled, (state, action) => {
            state.loading = false;
            state.draftsList = action.payload;
        });
        builder.addCase(fetchDrafts.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        });

        builder.addCase(fetchDraftById.pending, state => {
            state.fetchingDraft = true;
            state.error = null;
        });
        builder.addCase(fetchDraftById.fulfilled, (state, action) => {
            state.fetchingDraft = false;
            state.currentDraft = action.payload;
        });
        builder.addCase(fetchDraftById.rejected, (state, action) => {
            state.fetchingDraft = false;
            state.error = action.payload as string;
        });

        builder.addCase(createDraftFromOrders.pending, state => {
            state.creatingDraft = true;
            state.error = null;
        });
        builder.addCase(createDraftFromOrders.fulfilled, (state, action) => {
            state.creatingDraft = false;
            state.currentDraft = action.payload;
            state.draftsList.push(action.payload);
        });
        builder.addCase(createDraftFromOrders.rejected, (state, action) => {
            state.creatingDraft = false;
            state.error = action.payload as string;
        });

        builder.addCase(updateDraftOrders.pending, state => {
            state.updatingDraft = true;
            state.error = null;
        });
        builder.addCase(updateDraftOrders.fulfilled, (state, action) => {
            state.updatingDraft = false;
            state.currentDraft = action.payload;

            const index = state.draftsList.findIndex(
                draft => draft.shipmentId === action.payload.shipmentId,
            );
            if (index > -1) {
                state.draftsList[index] = action.payload;
            }
        });
        builder.addCase(updateDraftOrders.rejected, (state, action) => {
            state.updatingDraft = false;
            state.error = action.payload as string;
        });

        builder.addCase(buildShipmentFromDraft.pending, state => {
            state.buildingShipment = true;
            state.error = null;
        });
        builder.addCase(buildShipmentFromDraft.fulfilled, (state, action) => {
            state.buildingShipment = false;
            state.builtShipment = action.payload;

            state.draftsList = state.draftsList.filter(
                draft => draft.shipmentId !== action.payload.shipmentId,
            );
            state.currentDraft = null;
        });
        builder.addCase(buildShipmentFromDraft.rejected, (state, action) => {
            state.buildingShipment = false;
            state.error = action.payload as string;
        });

        builder.addCase(fetchSuggestedOrders.pending, state => {
            state.fetchingSuggestions = true;
            state.error = null;
        });
        builder.addCase(fetchSuggestedOrders.fulfilled, (state, action) => {
            state.fetchingSuggestions = false;
            state.suggestedOrders = action.payload;
        });
        builder.addCase(fetchSuggestedOrders.rejected, (state, action) => {
            state.fetchingSuggestions = false;
            state.error = action.payload as string;
        });

        builder.addCase(deleteDraft.pending, state => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(deleteDraft.fulfilled, (state, action) => {
            state.loading = false;
            state.draftsList = state.draftsList.filter(
                draft => draft.shipmentId !== action.payload,
            );
            if (state.currentDraft?.shipmentId === action.payload) {
                state.currentDraft = null;
            }
        });
        builder.addCase(deleteDraft.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        });
    },
});

export const {
    clearCurrentDraft,
    clearBuiltShipment,
    clearSuggestedOrders,
    clearError,
} = draftsSlice.actions;

export const selectDraftsList = (
    state: RootState,
): IShipmentDraftResponseModel[] => state.mcsDrafts.draftsList;
export const selectCurrentDraft = (
    state: RootState,
): IShipmentDraftResponseModel | null => state.mcsDrafts.currentDraft;
export const selectSuggestedOrders = (
    state: RootState,
): IDraftShipmentOrderModel[] => state.mcsDrafts.suggestedOrders;
export const selectBuiltShipment = (state: RootState): IShipment | null =>
    state.mcsDrafts.builtShipment;
export const selectDraftsLoading = (state: RootState): boolean =>
    state.mcsDrafts.loading;
export const selectFetchingDraft = (state: RootState): boolean =>
    state.mcsDrafts.fetchingDraft;
export const selectCreatingDraft = (state: RootState): boolean =>
    state.mcsDrafts.creatingDraft;
export const selectUpdatingDraft = (state: RootState): boolean =>
    state.mcsDrafts.updatingDraft;
export const selectBuildingShipment = (state: RootState): boolean =>
    state.mcsDrafts.buildingShipment;
export const selectFetchingSuggestions = (state: RootState): boolean =>
    state.mcsDrafts.fetchingSuggestions;
export const selectDraftsError = (state: RootState): string | null =>
    state.mcsDrafts.error;
export const selectHasDrafts = (state: RootState): boolean =>
    state.mcsDrafts.draftsList.length > 0;
export const selectDraftsCount = (state: RootState): number =>
    state.mcsDrafts.draftsList.length;

export default draftsSlice.reducer;
